export type RankingMode = 'kge0' | 'kge1' | 'mixed' | 'grade';

// What the "current"/percentile numbers are calculated from — defaults to the
// latest breakthrough activity (the server's default when data_source is omitted).
export type DataSource = 'breakthrough' | 'current_signature' | 'starting_signature' | 'final_signature' | 'activity' | 'historical_mmp' | 'best_signature';

export interface RankingActivityOption {
  path: string;
  name: string;
  start_date: string;
}

export interface MmpCurve {
  durations: number[];
  powers: number[];
}

// The 12 fixed ranking-discipline durations (seconds) — mirrors Progression::ATHLETES_CYCLING
// server-side. A historical-MMP envelope can span far more points than this (every duration any
// activity in range was ever computed at), so trim it down to just what rankings need before
// sending it back to the server.
export const RANKING_DURATIONS = [10, 120, 180, 240, 300, 360, 480, 600, 1200, 3600, 7200, 10800];

export function filterMmpCurveToRankingDurations(curve: MmpCurve): MmpCurve {
  const byDuration = new Map(curve.durations.map((d, i) => [d, curve.powers[i]]));
  const durations: number[] = [];
  const powers: number[] = [];
  for (const d of RANKING_DURATIONS) {
    const p = byDuration.get(d);
    if (p != null) {
      durations.push(d);
      powers.push(p);
    }
  }
  return { durations, powers };
}

export interface RadarSourceActivity {
  url: string;
  name: string;
  startDateLocal: string;
  signature: { pp: number; atc: number; ftp: number };
}

export interface RadarEntry {
  athlete: string;
  db_name: string;
  percentile: number;
  median: number;
  best: number;
  unit: string;
  current: number;
  desc: string;
  duration: number | null;
  // Only present when data_source=best_signature — which activity's own signature
  // produced this discipline's number (can differ per discipline).
  sourceActivity?: RadarSourceActivity | null;
}

export interface BestActivity {
  url: string;
  name: string;
  start_date_local: string;
  description: string;
  signature: any;
}

export interface UserStatsResponse {
  current: number | string;
  max: number | string;
  pop_avg: number;
  pop_max: number;
  pop_percentile: number;
  pop_unit: string;
  user_athlete_db: string;
  best_activity: BestActivity | null;
  radar: RadarEntry[];
  signature: any;
}

// "st"/"nd"/"rd"/"th" for a percentile number — 11-13 are always "th" (the exception
// to the normal 1/2/3 pattern), everything else keys off the last digit.
export function ordinalSuffix(n: number): string {
  const abs = Math.abs(Math.round(n));
  if (abs % 100 >= 11 && abs % 100 <= 13) return 'th';
  switch (abs % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

// Compact duration caption ("10 min", "1h 30m") for the radar/table — distinct
// from time.ts's format_duration, which produces a clock-style "MM:SS" readout.
export function formatEffortDuration(seconds: number | null): string {
  if (seconds == null) return '';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const minutes = Math.round(seconds / 60);
    return `${minutes} min`;
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

export function rankingTypeFor(mode: RankingMode, grade: number): string {
  return mode === 'grade' ? `grade_${grade}` : mode;
}

// Spelled-out duration ("3 Minute", "1 Hour") for prose captions like "3 Minute Power" —
// distinct from formatEffortDuration's compact axis/table form ("3 min", "1h").
export function spelledOutDuration(seconds: number | null): string {
  if (seconds == null) return '';
  if (seconds < 60) return `${seconds} Second`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} Minute`;
  return `${Math.round(seconds / 3600)} Hour`;
}

// "Time-Triallist" / "Century Rider" / "Endurance" are deprecated athlete-type names for
// the 1/2/3 hour power buckets — show a plain duration-based label instead everywhere.
const HOUR_POWER_LABELS: Record<string, string> = {
  'Time-Triallist': '1 Hour Power',
  'Century Rider': '2 Hour Power',
  'Endurance': '3 Hour Power',
};

export function isDeprecatedAthleteName(athlete: string): boolean {
  return athlete in HOUR_POWER_LABELS;
}

export function disciplineLabel(athlete: string): string {
  return HOUR_POWER_LABELS[athlete] ?? athlete;
}

// W/kg values are small (e.g. 4.23) and lose meaning rounded to a whole number; watts don't need decimals.
export function valuePrecision(unit: string): number {
  return unit === 'W/kg' ? 2 : 0;
}

// Duration-zone stops: [degrees clockwise from top, hex color]. Hue sweeps by
// effort duration — purple (sprint) -> red (2-4min) -> [orange, transition
// only] -> yellow (5min-20min) -> green (20min-1hr) -> blue (2-3hr) — with a
// hard cut (no blend) back to purple at the top seam. Single source of truth
// for both the radar's gradient and the table's per-row color coding.
export const ZONE_STOPS: [number, string][] = [
  [0, '#7e22ce'],
  [30, '#ef4444'],
  [90, '#ef4444'],
  [105, '#f97316'],
  [120, '#facc15'],
  [210, '#facc15'],
  [240, '#22c55e'],
  [270, '#22c55e'],
  [300, '#3b82f6'],
  [360, '#3b82f6'],
];

export function zoneConicGradient(): string {
  return `conic-gradient(from 0deg, ${ZONE_STOPS.map(([deg, hex]) => `${hex} ${deg}deg`).join(', ')})`;
}

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function rgbToHex([r, g, b]: [number, number, number]): string {
  return '#' + [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('');
}

// deg: degrees clockwise from top (0-360).
export function zoneColorAtDeg(deg: number): string {
  const d = ((deg % 360) + 360) % 360;
  for (let i = 0; i < ZONE_STOPS.length - 1; i++) {
    const [d0, c0] = ZONE_STOPS[i];
    const [d1, c1] = ZONE_STOPS[i + 1];
    if (d >= d0 && d <= d1) {
      const t = d1 === d0 ? 0 : (d - d0) / (d1 - d0);
      const rgb0 = hexToRgb(c0);
      const rgb1 = hexToRgb(c1);
      return rgbToHex([rgb0[0] + (rgb1[0] - rgb0[0]) * t, rgb0[1] + (rgb1[1] - rgb0[1]) * t, rgb0[2] + (rgb1[2] - rgb0[2]) * t]);
    }
  }
  return ZONE_STOPS[ZONE_STOPS.length - 1][1];
}

// Given an axis index into an n-entry radar (axis 0 = top, clockwise), its zone color.
export function zoneColorForIndex(i: number, n: number): string {
  return zoneColorAtDeg((i * 360) / n);
}
